import type { GeoJSONSource, Map as MlMap } from 'maplibre-gl'

/**
 * User-location GeoJSON source + accuracy-disk + inner-dot circle layers.
 *
 * Research §Code Examples Pattern 3 (lines 346-416). Decisions:
 *   - D-58: layers live on the MapLibre instance (imperative), not the DOM.
 *   - D-75: Google-Maps-style blue dot — inner 12px solid + accuracy disk
 *     whose pixel radius equals GPS `accuracy` meters at the current zoom.
 *
 * MapLibre's `circle-radius` is in screen pixels, but `GeolocationPosition.accuracy`
 * is in meters. We convert JS-side on every fix AND on every `map.on('zoom')`
 * event so the disk stays true-to-meters (Pitfall 2 lines 711-719, and UI-SPEC
 * line 408: "the disk's visual radius equals the GPS accuracy value in
 * real-world meters").
 */

export const SRC_ID = 'user-location-source'
export const LAYER_DISK = 'user-location-accuracy'
export const LAYER_DOT = 'user-location-dot'

export type Fix = {
  lat: number
  lng: number
  accuracy: number
  ts: number
}

export type Theme = 'day' | 'night'

const DAY_ACCENT = '#4285F4'
const NIGHT_ACCENT = '#74A7FF'

/** Web Mercator equator circumference in metres. */
const EQUATOR_METRES = 40_075_016.686

/**
 * Meters-per-pixel at a given latitude/zoom on the Web Mercator grid.
 *
 * Formula: `40075016.686 * cos(lat*π/180) / (256 * 2^zoom)`
 *
 * - At lat 0, zoom 0 → ~156,543 m/px.
 * - At lat 45, zoom 14 → ~1.09 m/px.
 * - At lat 45, zoom 18 → ~0.068 m/px.
 *
 * Latitude is clamped at ±85° (Web Mercator truncation — values outside
 * project to infinity).
 */
export function metersPerPixel(lat: number, zoom: number): number {
  if (zoom < 0) throw new RangeError(`metersPerPixel: zoom must be >=0 (got ${zoom})`)
  const clampedLat = Math.max(-85, Math.min(85, lat))
  return (
    (EQUATOR_METRES * Math.cos((clampedLat * Math.PI) / 180)) /
    (256 * Math.pow(2, zoom))
  )
}

function emptyFC() {
  return { type: 'FeatureCollection' as const, features: [] }
}

/**
 * Register the user-location source + two circle layers on the map.
 *
 * Idempotent — safe to call from `style.load` handlers after every
 * `map.setStyle` (D-64 + Pitfall 1). If the source already exists, bail.
 */
export function addUserLocationLayers(map: MlMap): void {
  if (map.getSource(SRC_ID)) return

  map.addSource(SRC_ID, {
    type: 'geojson',
    data: emptyFC(),
  })

  // Accuracy disk (UI-SPEC lines 391-406).
  map.addLayer({
    id: LAYER_DISK,
    type: 'circle',
    source: SRC_ID,
    paint: {
      'circle-radius': ['get', 'accuracyPx'],
      'circle-color': ['get', 'accentColor'],
      'circle-opacity': 0.18,
      'circle-stroke-width': 0,
    },
  })

  // Inner solid dot (UI-SPEC lines 411-424, 12px diameter = radius 6).
  map.addLayer({
    id: LAYER_DOT,
    type: 'circle',
    source: SRC_ID,
    paint: {
      'circle-radius': 6,
      'circle-color': ['get', 'accentColor'],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#FFFFFF',
    },
  })
}

/**
 * Write the current fix into the user-location source. Clamps `accuracyPx`
 * at a minimum of 4 pixels so the disk is always visible even at very low
 * zooms or very tight accuracy.
 */
export function updateUserLocation(
  map: MlMap,
  fix: Fix,
  theme: Theme,
): void {
  const src = map.getSource(SRC_ID) as GeoJSONSource | undefined
  if (!src) return
  const mpp = metersPerPixel(fix.lat, map.getZoom())
  const accuracyPx = Math.max(4, fix.accuracy / mpp)
  const accentColor = theme === 'night' ? NIGHT_ACCENT : DAY_ACCENT
  src.setData({
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [fix.lng, fix.lat] },
        properties: { accuracyPx, theme, accentColor },
      },
    ],
  })
}

/**
 * Clear the user-location dot — used when permission flips denied mid-session.
 */
export function clearUserLocation(map: MlMap): void {
  const src = map.getSource(SRC_ID) as GeoJSONSource | undefined
  if (!src) return
  src.setData(emptyFC())
}
